import { useRef, useEffect, useState, useMemo } from "react"
import { Marker } from "react-leaflet"

import type { FC, ReactElement } from "react"
import type { TooltipProps } from "react-leaflet"
import type {
  DivIcon,
  Icon,
  LatLngTuple,
  LeafletEventHandlerFnMap,
  Marker as LeafletMarker,
} from "leaflet"

import { MarkerTooltip } from "widgets/styled/map"

interface MapMarkerProps {
  icon: Icon | DivIcon
  position: LatLngTuple | undefined
  tooltipContent?: ReactElement | string | null
  tooltipProps?: TooltipProps
  popup?: ReactElement
  zIndexOffset?: number
  riseOffset?: number
  // Extra leaflet handlers. `mouseover`/`mouseout` run on top of the ones
  // driving the hover scale rather than replacing them.
  eventHandlers?: LeafletEventHandlerFnMap
  // Off for a marker with nothing to open: no pointer, no hover, not a tab stop.
  interactive?: boolean
}

function getTooltipKey(tooltipProps: TooltipProps = {}, position?: LatLngTuple) {
  const permanent = tooltipProps.permanent ? "permanent" : "normal"
  const pos = position ? position.join("-") : "no-pos"
  return `tooltip-${permanent}-${pos}`
}

const MapMarker: FC<MapMarkerProps> = ({
  popup,
  icon,
  position,
  zIndexOffset,
  tooltipContent,
  tooltipProps = {},
  riseOffset = 1000,
  eventHandlers,
  interactive = true,
}) => {
  const markerRef = useRef<LeafletMarker>(null)
  const [hovered, setHovered] = useState(false)

  // Memoized because react-leaflet rebinds every handler when this object
  // changes identity, and a caller that memoized its own would lose that.
  const markerEventHandlers = useMemo<LeafletEventHandlerFnMap>(
    () => ({
      ...eventHandlers,
      mouseover: (event) => {
        setHovered(true)
        eventHandlers?.mouseover?.(event)
      },
      mouseout: (event) => {
        setHovered(false)
        eventHandlers?.mouseout?.(event)
      },
    }),
    [eventHandlers],
  )

  useEffect(() => {
    const el = markerRef.current?.getElement()
    if (!el) return
    el.style.transformOrigin = "center center"
    if (hovered) {
      el.style.transform = `${el.style.transform} scale(1.15)`
    } else {
      el.style.transform = el.style.transform.replace(/\s*scale\([^)]*\)/, "")
    }
  }, [hovered])

  // Workaround: When the popup closes, the tooltip disappears even if it is marked as permanent.
  // To ensure the tooltip remains visible, we explicitly reopen it when the popup closes.
  useEffect(() => {
    if (!markerRef.current) return
    const marker = markerRef.current

    const handlePopupClose = () => {
      if (tooltipContent && tooltipProps.permanent) {
        marker.openTooltip()
      }
    }
    marker.on("popupclose", handlePopupClose)
    return () => {
      marker.off("popupclose", handlePopupClose)
    }
  }, [tooltipContent, tooltipProps.permanent])

  return (
    <>
      {position && (
        <Marker
          ref={markerRef}
          riseOnHover
          position={position}
          icon={icon}
          zIndexOffset={zIndexOffset}
          riseOffset={riseOffset}
          interactive={interactive}
          keyboard={interactive}
          autoPanOnFocus
          eventHandlers={markerEventHandlers}
        >
          {popup}
          {tooltipContent && (
            <MarkerTooltip
              direction="bottom"
              offset={[0, 15]}
              opacity={1}
              key={getTooltipKey(tooltipProps, position)}
              {...tooltipProps}
            >
              {tooltipContent}
            </MarkerTooltip>
          )}
        </Marker>
      )}
    </>
  )
}

export default MapMarker
